import type { User } from '@api/models';
import dayjs from 'dayjs';
import { defineStore } from 'pinia';

import type { TaskDashboardResponse } from '@stuut/api-sdk/models';

interface State {
  overviewData: TaskDashboardResponse | null;
  selectedAssignee: User | null; // null means "All" for admins
  selectedDay: dayjs.Dayjs;
  selectedWeekStart: dayjs.Dayjs;
}

export const useTaskOverviewStore = defineStore('taskOverview', {
  actions: {
    _updateSelectedDayForWeek() {
      const today = dayjs();
      const weekStart = this.selectedWeekStart;
      const weekEnd = weekStart.add(6, 'day');

      // If today is in this week, select today; otherwise select Monday
      if (today.isAfter(weekStart.subtract(1, 'day')) && today.isBefore(weekEnd.add(1, 'day'))) {
        this.selectedDay = today;
      } else {
        this.selectedDay = weekStart; // Monday
      }
    },

    goToMonth(month: dayjs.Dayjs) {
      this.selectedWeekStart = month.startOf('month');
      this._updateSelectedDayForWeek();
      this.setSelectedDay(month.startOf('month'));
    },

    goToToday() {
      const today = dayjs();
      this.selectedWeekStart = today.startOf('week');
      this.selectedDay = today;
    },

    nextDay() {
      this.selectedDay = this.selectedDay?.add(1, 'day')?.startOf('day');

      const weekEnd = this.selectedWeekStart?.add(6, 'day')?.startOf('day');

      if (this.selectedDay.diff(weekEnd) > 0) {
        this.selectedWeekStart = this.selectedWeekStart.add(1, 'week');
      }
    },

    nextWeek() {
      this.selectedWeekStart = this.selectedWeekStart.add(1, 'week');
      this._updateSelectedDayForWeek();
    },

    previousDay() {
      this.selectedDay = this.selectedDay?.subtract(1, 'day')?.startOf('day');

      if (this.selectedDay.diff(this.selectedWeekStart, 'days') < 0) {
        this.selectedWeekStart = this.selectedWeekStart.subtract(1, 'week');
      }
    },

    previousWeek() {
      this.selectedWeekStart = this.selectedWeekStart.subtract(1, 'week');
      this._updateSelectedDayForWeek();
    },

    setSelectedAssignee(user: User | null) {
      this.selectedAssignee = user;
    },

    setSelectedDay(day: dayjs.Dayjs) {
      this.selectedDay = day;
    },

    setSelectedWeek(weekStart: dayjs.Dayjs) {
      this.selectedWeekStart = weekStart.startOf('week');
    },
  },

  getters: {
    isSelectedDayToday(state): boolean {
      return state.selectedDay.isSame(dayjs(), 'day');
    },

    progressPercentage(state): number {
      return state.overviewData?.weeklyProgress?.percentage ?? 0;
    },

    weekDays(state): dayjs.Dayjs[] {
      return Array.from({ length: 7 }, (_, i) => state.selectedWeekStart.add(i, 'day'));
    },

    weekStartFormatted(state): string {
      return state.selectedWeekStart.format('YYYY-MM-DD');
    },
  },

  state: (): State => ({
    overviewData: null,
    selectedAssignee: null,
    selectedDay: dayjs(),
    selectedWeekStart: dayjs().startOf('week'),
  }),
});

export default useTaskOverviewStore;
